import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    //if (!this.#checkFormValidity()) return;

   
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Checks if the form is valid when the user adds an item to cart.
   * Currently only checks the gift card recipient form.
   * @returns {boolean} - True if the form is valid, false otherwise.
   */
  #checkFormValidity() {
    const form = this.closest('form');
    if (!form) return true;

    const allInputs = Array.from(form.querySelectorAll('input, select, textarea')).filter((input) =>
      input.id.includes('Recipient')
    );
    let allInputsValid = true;
    for (const input of allInputs) {
      if (
        !(
          input instanceof HTMLInputElement ||
          input instanceof HTMLSelectElement ||
          input instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      // Skip disabled inputs
      if (input.disabled) continue;

      // Check validity on all input elements
      if (!input.checkValidity()) {
        allInputsValid = false;
        break;
      }
    }
    return allInputsValid;
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #pendingRetries = new WeakMap();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  async #waitForCartEmpty(timeout = 20000, interval = 500) {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      try {
        const cart = await fetch('/cart.js').then(res => res.json());
        if (cart && Array.isArray(cart.items) && cart.items.length === 0) return true;
      } catch (err) {
        // ignore and retry
      }
      await new Promise(r => setTimeout(r, interval));
    }
    return false;
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    const currentCart = await fetch('/cart.js').then(res => res.json());
    const currentType = currentCart.attributes?.type || null;
    const activeType = currentCart.attributes?.activeType || null;

    if (activeType && currentType && activeType !== currentType && currentCart.items.length > 0) {
      document.dispatchEvent(new CustomEvent('open-purchase-blocking-popup'));
      
      const retryKey = 'handleSubmit';
      if (this.#pendingRetries.has(this) && this.#pendingRetries.get(this) === retryKey) return;
      this.#pendingRetries.set(this, retryKey);
      
      const clearClickHandler = async (/** @type {MouseEvent} */ e) => {
        if (!e.target) return;
        const target = /** @type {HTMLElement} */ (e.target);
        const el = target.closest && target.closest('.js-cart-clear');
        if (!el) return;
        
        document.removeEventListener('click', clearClickHandler);
        
        const emptied = await this.#waitForCartEmpty(20000, 500);
        this.#pendingRetries.delete(this);
        
        if (emptied) {
          setTimeout(() => {
            this.handleSubmit(event);
          }, 300);
        }
      };
      
      document.addEventListener('click', clearClickHandler);
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');
    const deliverySelector = document.querySelector('delivery-selector');
    const variantId = form.querySelector('[ref="variantId"]').value;
    const qtyEl = form.querySelector('[ref="quantityInput"]');
    const locationInput = deliverySelector?.querySelector('.js-location');
    // Pages without the legacy selector (PDP delivery widget) persist the
    // committed selection in localStorage — see delivery-widget.js #persist.
    const storedLocation = localStorage.getItem('delivery-ship-type') === 'local' ? localStorage.getItem('delivery-location') : null;
    const location = locationInput ? locationInput.value : storedLocation || 'nationwide';
    const locationAvailable = qtyEl?.getAttribute(`data-inventory-${location}`);
    const isMerch = qtyEl instanceof HTMLElement && qtyEl.dataset?.merch === 'true';
    const today = new Date().toLocaleDateString('en-GB', { 
      day: '2-digit',
      month: 'long', 
      year: 'numeric' 
    });
    const selectedDate = deliverySelector?.querySelector('.js-delivery-date')?.value || localStorage.getItem('delivery-date');
    const shouldCheckInventory = isMerch || (selectedDate && today === selectedDate);

    fetch('/cart.js').then((response) => {
      return response.json();
    }).then((data) => {
      let qtyInCart = 0;

      data.items.forEach((item) => {
        if(location !== 'nationwide') {
          if(+item.variant_id === +variantId && item.properties['deliveryLocalLocation'] === location) {
            qtyInCart += item.quantity;
          }
        } else {
          if(+item.variant_id === +variantId && !item.properties['deliveryLocalLocation']) {
            qtyInCart += item.quantity;
          }
        }
      });

      if(shouldCheckInventory && qtyEl && locationAvailable && qtyInCart + parseInt(qtyEl.value) > locationAvailable) {
        // Scoped to this form; the document-wide lookup hit the first form on the page.
        const errorElement = this.querySelector('[ref="quantityError"]');
        if (!errorElement) return;

        errorElement.textContent = `Only ${locationAvailable} available for your selected location.`;
        errorElement.classList.remove('hidden');

        clearTimeout(this.quantityErrorTimeout);
        this.quantityErrorTimeout = setTimeout(() => {
          errorElement.textContent = '';
          errorElement.classList.add('hidden');
        }, 10000);
        return;
      }
      
      const addToCartComponent = this.refs.addToCartButtonContainer;
      if (addToCartComponent && addToCartComponent instanceof AddToCartComponent) {
        addToCartComponent.animateAddToCart();
        if (event.target && !event.target.closest('.quick-add-modal')) {
          addToCartComponent.animateFlyToCart();
        }
      }
      const formData = new FormData(form);

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      });

      if (currentType) {
        formData.append('attributes[activeType]', currentType);
      }
      
      if(this.refs.addToCartButtonContainer?.refs.addToCartButton?.dataset.dozen === 'true') {
        formData.append('properties[_uniqueId]', Date.now().toString());
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });

      if(form.elements.submit && form.elements.submit.value === 'continue-to-checkout') {
        const emailError = document.querySelector('[ref="emailError"]')
        const submitError = document.querySelector('[ref="submitError"]')

        const properties = {
          _variantId: parseInt(formData.get('id')),
        };

        if(formData.has('properties[Recipient email]')) {
          emailError.classList.add('hidden');

          if(!formData.get('properties[Recipient email]') || !formData.get('properties[Recipient email]').match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            emailError.classList.remove('hidden');
            emailError.querySelector('span').textContent = 'Recipient email field is required. Please enter a valid email address.';
            return;
          }
          properties['Recipient email'] = formData.get('properties[Recipient email]');
          properties['Message'] = formData.get('properties[Message]');
          properties['Recipient name'] = formData.get('properties[Recipient name]');
          properties['Send on'] = formData.get('properties[Send on]');
          properties['__shopify_offset'] = formData.get('properties[__shopify_offset]');
          properties['__shopify_send_gift_card_to_recipient'] = "on";
        }

        form.elements.submit.disabled = true;

        fetch(`${window.globalConfig.apiUrl}checkout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              customer: {},
              shipments: [{
                delivery_date: window.dayjs().format('DD MMMM YYYY'),
                items: [{
                  variant_id: parseInt(formData.get('id')),
                  properties,
                  quantity: 1
                }]
              }],
              totals: false,
              customer_service: "Phone"
            })
        })
        .then(response => response.json())
        .then(data => {
          if(data.success) {
            window.location.href = data.invoice_url;
          } else {
            submitError.querySelector('span').textContent = data.error;
            submitError.classList.remove('hidden');
          }
        })
        .catch(e => {
          submitError.querySelector('span').textContent = e.message;
          submitError.classList.remove('hidden');
        })
        .finally(() => {
          form.elements.submit.disabled = false;
        })
        return;
      }

      fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.dispatchEvent(
              new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
            );

            if (!addToCartTextError) return;
            addToCartTextError.classList.remove('hidden');

            // Reuse the text node if the user is spam-clicking
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              const newTextNode = document.createTextNode(response.message);
              addToCartTextError.appendChild(newTextNode);
            }

            // Create or get existing error live region for screen readers
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add('hidden');

              // Clear the announcement
              this.#clearLiveRegionText();
            }, 10000);

            // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
            // because our back-end still adds the max allowed amount to the cart.
            this.dispatchEvent(
              new CartAddEvent({}, this.id, {
                didError: true,
                source: 'product-form-component',
                itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                productId: this.dataset.productId,
              })
            );

            return;
          } else {
            const id = formData.get('id');

            if (addToCartTextError) {
              addToCartTextError.classList.add('hidden');
              addToCartTextError.removeAttribute('aria-live');
            }

            if (!id) throw new Error('Form ID is required');

            // Add aria-live region to inform screen readers that the item was added
            if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
              const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
              const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
              const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

              this.#setLiveRegionText(addedText);

              setTimeout(() => {
                this.#clearLiveRegionText();
              }, 5000);
            }

            this.dispatchEvent(
              new CartAddEvent({}, id.toString(), {
                source: 'product-form-component',
                itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                productId: this.dataset.productId,
                sections: response.sections,
              })
            );
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          // add more thing to do in here if needed.
          cartPerformance.measureFromEvent('add:user-action', event);
        });
    });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
